import { ClickTarget } from "@/services/Analytics/EventTypes";
import {
  WfHeader,
  WfIconContentUserRegular,
  WfIconNavigationChevronRightRegular,
  WfMenuList,
  WfMenuListItem,
} from "@WahooFitness/redesignr";
import { t } from "@lingui/macro";
import { Avatar, Box, Skeleton, Typography } from "@mui/material";
import { useCallback } from "react";
import { ReactRouterLinkWrapper } from "../../MenuList/ReactRouterLinkWrapper";
import { useNavigate } from "react-router";
import { UserType } from "@WahooFitness/cloud-client-types";
import useUserDisplay from "@/hooks/useUserDisplay";

interface AccountSectionProps {
  currentUser?: UserType;
  subscriptionStatus: string;
  logSettingsMenuEvent: (clickTarget: ClickTarget) => void;
}

function AccountSection({
  currentUser,
  subscriptionStatus,
  logSettingsMenuEvent,
}: AccountSectionProps) {
  const navigate = useNavigate();

  const goToMembership = useCallback(() => {
    logSettingsMenuEvent(ClickTarget.my_subscription);
    navigate("/membership");
  }, [logSettingsMenuEvent, navigate]);

  const { userDisplayName, userInitials } = useUserDisplay(currentUser);

  return (
    <>
      <WfHeader
        title={t`Account`}
        hierarchy="H1"
        data-testid="accountHeader"
        description={currentUser?.email ?? undefined}
      />
      <WfMenuList>
        <WfMenuListItem
          id="account-user"
          isHeader
          content={
            userDisplayName ?? (
              <Box py={3 / 8}>
                <Skeleton width={150} height="1rem" variant="rectangular" />
              </Box>
            )
          }
          startIcon={
            currentUser ? (
              <Avatar
                src={currentUser.avatar?.url ?? undefined}
                sx={{
                  color: "text.primary",
                  bgcolor: "secondary.main",
                  height: 44,
                  width: 44,
                }}
              >
                <Typography variant="ui-md">
                  {userInitials || <WfIconContentUserRegular fontSize="large" />}
                </Typography>
              </Avatar>
            ) : (
              <Skeleton height={44} width={44} variant="circular" />
            )
          }
          href="/athlete-profile"
          variant="internalLink"
          linkComponent={ReactRouterLinkWrapper}
          data-testid="settings.profile"
          disabled={!currentUser}
        />
        <WfMenuListItem
          id="account-membership"
          content={t`Wahoo Membership`}
          variant="action"
          action={goToMembership}
          secondaryRightComponentOverride={
            <Box display="flex" flexDirection="row" gap={2} alignItems="center">
              <Typography variant="prose-base">{subscriptionStatus}</Typography>
              <WfIconNavigationChevronRightRegular color="action" />
            </Box>
          }
          data-testid="settings.subscription"
          disabled={!currentUser}
        />
      </WfMenuList>
    </>
  );
}

export default AccountSection;
